import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy as syntaxStyle } from 'react-syntax-highlighter/dist/esm/styles/prism';
import '../styles/CustomMarkdown.css'; // We'll create this next

// Markdown normalization function
const normalizeMarkdown = (markdownText) => {
  if (!markdownText) return '';
  
  // Step 1: Ensure consistent heading formats
  let normalized = markdownText.replace(/^(#+)(?!\s)/gm, '$1 ');
  
  // Step 2: Normalize list formatting (both bullet points and numbered lists)
  normalized = normalized.replace(/^(\s*)[*+-](?!\s)/gm, '$1- ');
  
  // Step 3: Ensure proper spacing between Markdown blocks
  normalized = normalized.replace(/\n{3,}/g, '\n\n');
  
  // Step 4: Handle nested lists consistently
  normalized = normalized.replace(/^(\s*)[-*+] (.*)\n+(\s+)[-*+] /gm, '$1- $2\n$3- ');
  
  // Step 5: Standardize percentage formats
  normalized = normalized.replace(/(\d+)(\s*)%/g, '$1%');
  
  // Step 6: Standardize section headers for consistent identification
  const sectionHeaders = [
    'Skills Match Analysis', 'Experience Alignment', 'Recommendations',
    'Strengths', 'Gaps', 'Overall Assessment', 'Overall Alignment Score'
  ];
  
  sectionHeaders.forEach(header => {
    const headerRegex = new RegExp(`(#+)\\s*${header}\\s*:?`, 'gi');
    normalized = normalized.replace(headerRegex, `### ${header}`);
  });

  return normalized;
};

// Function to extract overall score from markdown
export const extractPercentage = (markdownText) => {
  if (!markdownText) return 0;
  
  // Look for "Overall Alignment Score: X%" pattern
  const alignmentMatch = markdownText.match(/Overall Alignment Score:\s*(\d+(\.\d+)?)\s*%/i);
  if (alignmentMatch && alignmentMatch[1]) {
    return parseFloat(alignmentMatch[1]);
  }
  
  // Fallback: Look for any percentage in the text
  const percentageMatch = markdownText.match(/(\d+(\.\d+)?)\s*%/);
  if (percentageMatch && percentageMatch[1]) {
    return parseFloat(percentageMatch[1]);
  }
  
  return 0;
};

const CustomMarkdownRenderer = ({ markdownContent }) => {
  // Apply normalization before rendering
  const normalizedContent = normalizeMarkdown(markdownContent);
  
  return (
    <div className="markdown-container">
      <ReactMarkdown
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={syntaxStyle}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          // Add additional custom renderers for consistent formatting
          h1: ({node, ...props}) => <h1 className="markdown-h1" {...props} />,
          h2: ({node, ...props}) => <h2 className="markdown-h2" {...props} />,
          h3: ({node, ...props}) => <h3 className="markdown-h3" {...props} />,
          ul: ({node, ...props}) => <ul className="markdown-list" {...props} />,
          li: ({node, ...props}) => <li className="markdown-list-item" {...props} />
        }}
      >
        {normalizedContent}
      </ReactMarkdown>
    </div>
  );
};

export default CustomMarkdownRenderer;